import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import toast from 'components/partials/toast/toast';
import type { SelectOption } from 'ui/select';

import {
  CREATE_SUCCESS,
  INITIAL_CREATE_BLOCKS,
  MAX_DIMENSIONS,
  PREVIEW_ERROR_FALLBACK,
  UPDATE_SUCCESS,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.constants';
import {
  haveDimensionsChanged,
  normalizeCatalog,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.helpers';
import {
  EMPTY_DIMENSION,
  buildFormSchema,
  rowToFormValues,
  toPreviewDimensions,
  toCreateRequest,
  toUpdateRequest,
  validateTitle,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.form';
import type {
  CaptureFormValues,
  DimensionDefinition,
  FormDrawerMode,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.types';
import { notifyCaptureError } from 'containers/admin/clients/client/tabs-components/critical-populations/notify-capture-error';

import type { CriticalPopulationResponse } from 'api/generated/schemas';

import {
  getApiErrorDetail,
  getApiErrorStatus,
} from 'helpers/api/get-api-error-detail';

import {
  useCreateCriticalPopulation,
  useCriticalPopulationDimensions,
  useCriticalPopulationTitlePreview,
  useUpdateCriticalPopulation,
} from 'hooks/api/use-critical-populations';

interface UseCaptureFormArgs {
  clientId: number;
  mode: FormDrawerMode;
  row?: CriticalPopulationResponse;
  enteringClassYear: number;
  isMonitoringPending: boolean;
  onClose: () => void;
}

export interface DimensionBlockModel {
  key: string;
  attribute: string;
  values: string[];
  definition: DimensionDefinition | undefined;
  attributeOptions: SelectOption[];
  valueOptions: SelectOption[];
  attributeError: string | undefined;
  valuesError: string | undefined;
}

interface UseCaptureFormResult {
  isCatalogLoading: boolean;
  isCatalogError: boolean;
  retryCatalog: () => void;
  blocks: DimensionBlockModel[];
  canAddDimension: boolean;
  canRemoveDimension: boolean;
  previewError: string | undefined;
  titleValue: string;
  titleError: string | undefined;
  canRestoreTitle: boolean;
  noteValue: string;
  noteError: string | undefined;
  isSubmitDisabled: boolean;
  isPending: boolean;
  handleAddDimension: () => void;
  handleRemoveDimension: (index: number) => void;
  handleAttributeChange: (index: number, attribute: string) => void;
  handleValuesChange: (index: number, values: string[]) => void;
  handleTitleChange: (title: string) => void;
  handleRestoreTitle: () => void;
  handleNoteChange: (note: string) => void;
  isConfirmOpen: boolean;
  handleSubmit: () => Promise<void>;
  handleConfirmSave: () => Promise<void>;
  handleCancelConfirm: () => void;
}

const toOption = (value: string, label = value): SelectOption => ({
  value,
  label,
});

const getDefaultValues = (
  mode: FormDrawerMode,
  row: CriticalPopulationResponse | undefined
): CaptureFormValues => {
  if (mode === 'edit' && row) return rowToFormValues(row);

  return {
    dimensions: Array.from({ length: INITIAL_CREATE_BLOCKS }, () => ({
      ...EMPTY_DIMENSION,
    })),
    note: '',
  };
};

interface GeneratedTitleArgs {
  showServerSummary: boolean;
  serverSummary: string | undefined;
  isPreviewActive: boolean;
  isPreviewError: boolean;
  previewTitle: string | undefined;
}

const getGeneratedTitle = ({
  showServerSummary,
  serverSummary,
  isPreviewActive,
  isPreviewError,
  previewTitle,
}: GeneratedTitleArgs): string | undefined => {
  if (showServerSummary) return serverSummary;

  return isPreviewActive && !isPreviewError ? previewTitle : undefined;
};

export const useCaptureForm = ({
  clientId,
  mode,
  row,
  enteringClassYear,
  isMonitoringPending,
  onClose,
}: UseCaptureFormArgs): UseCaptureFormResult => {
  const catalogQuery = useCriticalPopulationDimensions();
  const createMutation = useCreateCriticalPopulation(clientId);
  const updateMutation = useUpdateCriticalPopulation(clientId);

  const [titleOverride, setTitleOverride] = useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const catalog = useMemo(
    () => normalizeCatalog(catalogQuery.data),
    [catalogQuery.data]
  );
  const isCatalogReady = !!catalog.length;

  const resolver = useMemo(
    () => yupResolver(buildFormSchema(catalog)),
    [catalog]
  );

  const {
    control,
    setValue,
    trigger,
    handleSubmit,
    formState: { errors, isValid, isDirty },
  } = useForm<CaptureFormValues>({
    mode: 'onChange',
    defaultValues: getDefaultValues(mode, row),
    resolver,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'dimensions',
  });
  const dimensions = useWatch({ control, name: 'dimensions' });
  const noteValue = useWatch({ control, name: 'note' }) ?? '';

  useEffect(() => {
    if (mode === 'edit' && isCatalogReady) trigger('dimensions');
  }, [mode, isCatalogReady, trigger]);

  const blocks = fields.map((field, index): DimensionBlockModel => {
    const current = dimensions[index] ?? EMPTY_DIMENSION;
    const definition = catalog.find(
      ({ attribute }) => attribute === current.attribute
    );
    const usedElsewhere = new Set(
      dimensions
        .filter((_, otherIndex) => otherIndex !== index)
        .map(({ attribute }) => attribute)
    );

    return {
      key: field.id,
      attribute: current.attribute,
      values: current.values,
      definition,
      attributeOptions: catalog
        .filter(({ attribute }) => !usedElsewhere.has(attribute))
        .map(({ attribute, displayName }) => toOption(attribute, displayName)),
      valueOptions: (definition?.values ?? []).map((value) => toOption(value)),
      attributeError: errors.dimensions?.[index]?.attribute?.message,
      valuesError:
        errors.dimensions?.[index]?.values?.message ??
        errors.dimensions?.[index]?.values?.root?.message,
    };
  });

  const serverSummary = row?.summary;
  const showServerSummary = mode === 'edit' && !isDirty && !!serverSummary;
  const previewDimensions = useMemo(
    () => toPreviewDimensions(dimensions, catalog),
    [dimensions, catalog]
  );
  const previewQuery = useCriticalPopulationTitlePreview({
    clientId,
    dimensions: previewDimensions ?? [],
    enabled: !!previewDimensions && !showServerSummary,
  });

  const isPreviewActive = !!previewDimensions && !showServerSummary;
  const isPreviewRejected =
    isPreviewActive &&
    previewQuery.isError &&
    getApiErrorStatus(previewQuery.error) === 400;
  const previewError = isPreviewRejected
    ? getApiErrorDetail(previewQuery.error, PREVIEW_ERROR_FALLBACK)
    : undefined;

  const generatedTitle = getGeneratedTitle({
    showServerSummary,
    serverSummary,
    isPreviewActive,
    isPreviewError: previewQuery.isError,
    previewTitle: previewQuery.data?.title,
  });
  const titleValue = titleOverride ?? generatedTitle ?? '';
  const titleError =
    titleOverride !== null ? validateTitle(titleOverride) : undefined;
  const canRestoreTitle =
    titleOverride !== null && titleOverride !== generatedTitle;

  const getTitleToSend = (): string | undefined => {
    if (titleOverride === null) return undefined;

    const trimmed = titleOverride.trim();

    return trimmed === generatedTitle ? undefined : trimmed;
  };
  const isTitleChanged = getTitleToSend() !== undefined;

  const handleSave = async (values: CaptureFormValues): Promise<void> => {
    try {
      if (mode === 'edit' && row?.id !== undefined) {
        await updateMutation.mutateAsync({
          id: row.id,
          data: toUpdateRequest(values, getTitleToSend()),
        });
        toast.success(UPDATE_SUCCESS);
      } else {
        await createMutation.mutateAsync(
          toCreateRequest(values, enteringClassYear, getTitleToSend())
        );
        toast.success(CREATE_SUCCESS);
      }
      onClose();
    } catch (error) {
      if (notifyCaptureError(error)) onClose();
    }
  };

  const submit = handleSubmit(handleSave);
  const requiresMonitoringConfirm =
    mode === 'edit' &&
    row?.status === 'PROMOTED' &&
    !isMonitoringPending &&
    haveDimensionsChanged(row.dimensions, dimensions);

  return {
    isCatalogLoading: catalogQuery.isLoading,
    isCatalogError: catalogQuery.isError,
    retryCatalog: () => {
      catalogQuery.refetch();
    },
    blocks,
    canAddDimension: fields.length < MAX_DIMENSIONS,
    canRemoveDimension: fields.length > 1,
    previewError,
    titleValue,
    titleError,
    canRestoreTitle,
    noteValue,
    noteError: errors.note?.message,
    isSubmitDisabled:
      !isCatalogReady ||
      !isValid ||
      isPreviewRejected ||
      !!titleError ||
      (mode === 'edit' && !isDirty && !isTitleChanged),
    isPending: createMutation.isPending || updateMutation.isPending,
    handleAddDimension: () => append({ ...EMPTY_DIMENSION }),
    handleRemoveDimension: (index) => remove(index),
    handleAttributeChange: (index, attribute) => {
      setValue(`dimensions.${index}.attribute`, attribute, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue(`dimensions.${index}.values`, [], { shouldDirty: true });
    },
    handleValuesChange: (index, values) =>
      setValue(`dimensions.${index}.values`, values, {
        shouldDirty: true,
        shouldValidate: true,
      }),
    handleTitleChange: setTitleOverride,
    handleRestoreTitle: () => setTitleOverride(null),
    handleNoteChange: (note) =>
      setValue('note', note, { shouldDirty: true, shouldValidate: true }),
    isConfirmOpen,
    handleSubmit: async () => {
      if (requiresMonitoringConfirm) {
        setIsConfirmOpen(true);

        return;
      }
      await submit();
    },
    handleConfirmSave: async () => {
      setIsConfirmOpen(false);
      await submit();
    },
    handleCancelConfirm: () => setIsConfirmOpen(false),
  };
};
